import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { RegisteredInfo, User } from '../commonTypes';
import { failedResponses } from '../errors/responses';
import ListResponse from './listResponse';
import { Region } from './region';

const c = initContract();

export const Item = z.object({
  itemId: z.string(),
  quantity: z.number()
});

export type Item = z.infer<typeof Item>;

export const Group = z.object({
  name: z.string(),
  id: z.string(),
  quantity: z.number(),
  equipment: Item.array()
});

export type Group = z.infer<typeof Group>;

export const Basket = z.object({
  groups: Group.array()
});

export type Basket = z.infer<typeof Basket>;

export const CreditCheck = z.object({
  id: z.string().optional().or(z.null()),
  name: z.string().optional().or(z.null())
});

export type CreditCheck = z.infer<typeof CreditCheck>;

export const CompanyRegisteredInfo = RegisteredInfo;

export type CompanyRegisteredInfo = z.infer<typeof CompanyRegisteredInfo>;

export enum ApprovalStatus {
  Pending = 0,
  InviteApproved = 1,
  Not = 2,
  View = 3,
  Capital = 4,
  Full = 5
}

export enum ContractStatus {
  Pending = 0,
  Applied = 1,
  NdaSigned = 2,
  ContractSigned = 3
}

export const CompanyPropertyBag = z.object({
  allowCustomLogo: z.boolean().optional(),
  selectedProductIds: z.array(z.string()).optional()
});

export const Company = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  ingramId: z.string(),
  disabled: z.boolean(),
  approvalStatus: z.nativeEnum(ApprovalStatus),
  registeredInfo: CompanyRegisteredInfo,
  catalogId: z.string(),
  contractStatus: z.nativeEnum(ContractStatus),
  signatoryId: z.string().optional().or(z.null()),
  basket: Basket.optional().or(z.null()),
  activePlan: z.string().optional().or(z.null()),
  proposedPlan: z.string().optional().or(z.null()),
  creditCheck: CreditCheck.optional().or(z.null()),
  needsJobBundleApproval: z.boolean(),
  propertyBag: CompanyPropertyBag,
  customerCreditCheckId: z.string().optional(),
  region: Region,
  enableCustomNotifications: z.boolean().optional(),
  haloClientId: z.string().optional().nullable(),
  haloGeneralUserId: z.string().optional().nullable(),
  isProductToursEnabled: z.boolean().optional(),
  addHaloStockSites: z.array(z.number()).optional().nullable()
});

export type Company = z.infer<typeof Company>;

const CompanyCode = z.object({
  name: z.string(),
  code: z.string()
});

export type CompanyCode = z.infer<typeof CompanyCode>;

const CompanyCodeListResponse = ListResponse(CompanyCode);
type CompanyCodeListResponse = z.infer<typeof CompanyCodeListResponse>;

export const CompanyLookup = z.object({
  id: z.string(),
  country: z.string(),
  regNo: z.string(),
  safeNo: z.string(),
  type: z.string(),
  status: z.string(),
  vatNo: z.array(z.string()).nullable(),
  name: z.string(),
  address: z.object({
    simpleValue: z.string(),
    postCode: z.string()
  })
});

export type CompanyLookup = z.infer<typeof CompanyLookup>;

export const CompanyCheck = z.object({
  failedSections: z.array(z.string()).nullable()
});

export type CompanyCheck = z.infer<typeof CompanyCheck>;

export const Asset = z.object({
  deviceType: z.string().optional().or(z.null()),
  manufactorer: z.string().optional().or(z.null()),
  model: z.string().optional().or(z.null()),
  processor: z.string().optional().or(z.null()),
  ram: z.string().optional().or(z.null()),
  screenSize: z.string().optional().or(z.null()),
  storageSize: z.string().optional().or(z.null()),
  storageType: z.string().optional().or(z.null())
});

export type Asset = z.infer<typeof Asset>;

export const Agreement = z.object({
  id: z.string(),
  creationDateTime: z.string(),
  basket: Basket,
  assetList: z
    .object({
      assetItems: z.array(Asset)
    })
    .optional()
    .or(z.null())
});

export type Agreement = z.infer<typeof Agreement>;

export const Hardware = z.object({
  id: z.string(),
  company: Company,
  manifest: z.object({
    data: z.array(z.any())
  })
});

export const HardwareListResponse = z.object({
  items: z.array(Hardware)
});

export type Hardware = z.infer<typeof Hardware>;

export type CompanyUser = z.infer<typeof User>;

export const MspDetailsForCompany = z.object({
  name: z.string(),
  address: z.string(),
  region: Region
});

export type MspDetailsForCompany = z.infer<typeof MspDetailsForCompany>;

export const CustomerSelectedFinanceModel = z.object({
  id: z.string(),
  customerId: z.string(),
  peripheralFinanceModelId: z.string(),
  financeModelIds: z.array(z.string())
});

export type CustomerSelectedFinanceModel = z.infer<typeof CustomerSelectedFinanceModel>;

export const CustomerSelectedFinanceModelRequest = z.object({
  peripheralFinanceModelId: z.string().optional(),
  financeModelIds: z.array(z.string()).optional()
});

export type CustomerSelectedFinanceModelRequest = z.infer<typeof CustomerSelectedFinanceModelRequest>;

export const HaloTicketDto = z.object({
  id: z.string().optional(),
  ticketId: z.string().optional(), // In case the backend uses ticketId instead of id
  companyId: z.string().optional(),
  summary: z.string().optional(),
  status: z.string().optional(),
  dateCreated: z.string().optional()
});

export type HaloTicketDto = z.infer<typeof HaloTicketDto>;

export const HaloMappingDto = z.object({
  haloClientId: z.string().optional().nullable(),
  haloGeneralUserId: z.string().optional().nullable()
});

export type HaloMappingDto = z.infer<typeof HaloMappingDto>;

export const HaloStockDto = z.object({
  customerId: z.string(),
  item: z.string(),
  itemType: z.string().optional(),
  sku: z.string(),
  stockLocation: z.string(),
  siteID: z.string(),
  stockQuantity: z.number()
});

export type HaloStockDto = z.infer<typeof HaloStockDto>;

export const HaloEquipmentDto = z.object({
  customerId: z.string(),
  user: z.string(),
  type: z.string(),
  sku: z.string(),
  serial: z.string(),
  location: z.string(),
  LifecyclePack: z.string().optional(),
  hasLifecyclePack: z.boolean().optional(),
  hasAdrWarranty: z.boolean().optional(),
  warranty: z.string().optional(),
  warrantyExpire: z.string().optional(),
});
export type HaloEquipmentDto = z.infer<typeof HaloEquipmentDto>;

export const CustomerBundleItemDto = z.object({
  catalogItem: z.string(),
  count: z.number()
});

export const CustomerBundleDto = z.object({
  id: z.string(),
  name: z.string(),
  mainDevice: z.string().optional().nullable(),
  bundledItems: z.array(CustomerBundleItemDto)
});

export type CustomerBundleDto = z.infer<typeof CustomerBundleDto>;

export default c.router({
  getCompanies: {
    method: 'GET',
    path: '/partners/:partnerId/companies',
    responses: {
      200: z.object({
        items: z.array(Company)
      }),
      ...failedResponses
    }
  },
  getCompany: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id',
    responses: {
      200: Company,
      ...failedResponses
    }
  },
  getSelectedProducts: {
    method: 'GET',
    path: '/msp/:partnerId/customer/:id/selectedproducts',
    responses: {
      200: z.object({
        items: z.array(z.string())
      }),
      ...failedResponses
    }
  },
  putSelectedProducts: {
    method: 'PUT',
    path: '/msp/:partnerId/customer/:customerId/selectedproducts',
    responses: {
      200: z.object({ status: z.string() }),
      ...failedResponses
    },
    body: z.array(z.string()),
    summary: 'Update selected products'
  },
  getCompanyContract: {
    method: 'GET',
    path: '/msp/:partnerId/customer/:id/contract',
    responses: {
      200: z.object({
        signedDocumentPath: z.string(),
        certificatePath: z.string()
      }),
      ...failedResponses
    }
  },
  getCompanyPurchaseOrderApproval: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:id/purchaseorderbundles/approval',
    responses: {
      200: z.object({ needsApproval: z.boolean() }),
      ...failedResponses
    }
  },
  putCompanyPurchaseOrderApproval: {
    method: 'PUT',
    path: '/msp/:partnerId/customers/:customerId/purchaseorderbundles/approval',
    responses: {
      200: z.object({ status: z.string() }),
      ...failedResponses
    },
    body: z.object({
      approved: z.boolean()
    }),
    summary: 'Update purchase order approval status'
  },
  getCompanyContractApproval: {
    method: 'GET',
    path: '/msp/:partnerId/customers/:id/catalog/approval',
    responses: {
      200: z.object({ needsApproval: z.boolean() }),
      ...failedResponses
    }
  },
  putCompanyContractApproval: {
    method: 'PUT',
    path: '/msp/:partnerId/customers/:customerId/catalog/approval',
    responses: {
      200: Company,
      ...failedResponses
    },
    body: z.object({
      approved: z.boolean()
    }),
    summary: 'Update catalog approval status'
  },
  getCompanyNda: {
    method: 'GET',
    path: '/msp/:partnerId/customer/:id/nda',
    responses: {
      200: z.object({
        signedDocumentPath: z.string(),
        certificatePath: z.string()
      }),
      ...failedResponses
    }
  },
  createCompanyNda: {
    method: 'POST',
    path: '/msp/:mspId/customer/:customerId/nda',
    responses: {
      200: z.object({
        viewUrl: z.string()
      }),
      ...failedResponses
    },
    body: z.object({
      regNumber: z.string(),
      regAddress: z.string(),
      signerEmail: z.string(),
      signerName: z.string(),
      signerPosition: z.string(),
      returnUrl: z.string()
    })
  },
  createCompanyContract: {
    method: 'POST',
    path: '/msp/:mspId/customer/:customerId/contract',
    responses: {
      200: z.object({
        viewUrl: z.string()
      }),
      ...failedResponses
    },
    body: z.object({
      returnUrl: z.string()
    })
  },
  updateCompanyContract: {
    method: 'PUT',
    path: '/msp/:mspId/customer/:customerId/contract',
    responses: {
      200: z.object({
        status: z.string()
      }),
      ...failedResponses
    },
    body: z.object({})
  },
  createCompany: {
    method: 'POST',
    path: '/partners/:partnerId/companies',
    responses: {
      200: Company,
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      code: z.string(),
      registeredInfo: CompanyRegisteredInfo,
      propertyBag: CompanyPropertyBag,
      isProductToursEnabled: z.boolean().optional(),
      addHaloStockSites: z.array(z.number()).optional().nullable()
    }),
    summary: 'Create a company'
  },
  createCompanies: {
    method: 'POST',
    path: '/partners/:partnerId/companies/multi',
    responses: {
      200: z.object({ items: z.array(Company) }),
      ...failedResponses
    },
    body: z.array(
      z.object({
        name: z.string(),
        code: z.string(),
        registeredInfo: CompanyRegisteredInfo,
        propertyBag: CompanyPropertyBag,
        isProductToursEnabled: z.boolean().optional(),
        addHaloStockSites: z.array(z.number()).optional().nullable()
      })
    ),
    summary: 'Create companies'
  },
  updateCompanies: {
    method: 'PUT',
    path: '/partners/:partnerId/companies/multi',
    responses: {
      200: ListResponse(Company),
      ...failedResponses
    },
    body: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        code: z.string(),
        registeredInfo: CompanyRegisteredInfo,
        disabled: z.boolean(),
        propertyBag: CompanyPropertyBag,
        approvalStatus: z.nativeEnum(ApprovalStatus),
        isProductToursEnabled: z.boolean().optional(),
        addHaloStockSites: z.array(z.number()).optional().nullable()
      })
    ),
    summary: 'Update companies'
  },
  deleteCompany: {
    method: 'DELETE',
    path: '/partners/:partnerId/companies/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Delete a company'
  },
  updateCompany: {
    method: 'PUT',
    path: '/partners/:partnerId/companies/:id',
    responses: {
      200: Company,
      ...failedResponses
    },
    body: z.object({
      partnerId: z.string(),
      name: z.string(),
      code: z.string(),
      approvalStatus: z.nativeEnum(ApprovalStatus),
      disabled: z.boolean(),
      registeredInfo: CompanyRegisteredInfo,
      propertyBag: CompanyPropertyBag,
      enableCustomNotifications: z.boolean().optional(),
      haloClientId: z.string().optional().nullable(),
      haloGeneralUserId: z.string().optional().nullable(),
      isProductToursEnabled: z.boolean().optional(),
      addHaloStockSites: z.array(z.number()).optional().nullable()
    }),
    summary: 'Update a company'
  },
  updateCompanyPropertyBag: {
    method: 'PUT',
    path: '/partners/:partnerId/companies/:id',
    responses: {
      200: Company,
      ...failedResponses
    },
    body: z.object({
      partnerId: z.string(),
      name: z.string(),
      code: z.string(),
      ingramId: z.string(),
      approvalStatus: z.nativeEnum(ApprovalStatus),
      disabled: z.boolean(),
      registeredInfo: CompanyRegisteredInfo,
      propertyBag: CompanyPropertyBag
    }),
    summary: 'Update a companies property bag'
  },
  lookupCompany: {
    method: 'GET',
    path: '/partners/:partnerId/companies/creditsafe/search',
    query: z.object({
      companyname: z.string(),
      postcode: z.string()
    }),
    responses: {
      200: z.object({
        companies: z.array(CompanyLookup)
      }),
      ...failedResponses
    },
    summary: 'Lookup a company'
  },
  lookupCompanyBySafeNo: {
    method: 'GET',
    path: '/partners/:partnerId/companies/creditsafe/search',
    query: z.object({
      safeNo: z.string()
    }),
    responses: {
      200: z.object({
        companies: z.array(CompanyLookup)
      }),
      ...failedResponses
    }
  },
  checkCompany: {
    method: 'GET',
    path: '/partners/:partnerId/companies/creditsafe/report/:reportId',
    responses: {
      200: CompanyCheck,
      ...failedResponses
    }
  },
  updateBasket: {
    method: 'POST',
    path: '/partners/:partnerId/companies/:id/basket',
    responses: {
      200: Basket,
      ...failedResponses
    },
    body: Basket,
    summary: 'Update a company basket'
  },
  submitBasket: {
    method: 'POST',
    path: '/partners/:partnerId/companies/:id/quote',
    responses: {
      200: z.string(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Submit a company basket'
  },
  onboardCompany: {
    method: 'PATCH',
    path: '/partners/:partnerId/companies/:id',
    responses: {
      200: z.undefined()
    },
    body: z.object({
      planId: z.string(),
      creditCheckId: z.string()
    }),
    ...failedResponses
  },
  partnerOnboardCompany: {
    method: 'PATCH',
    path: '/partners/:partnerId/plans',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.object({
      planId: z.string(),
      creditCheckId: z.string(),
      companyId: z.string()
    }),
    summary: 'Onboard a company'
  },
  getTemplate: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/assets/template',
    responses: {
      200: z.instanceof(Blob),
      ...failedResponses
    }
  },
  uploadAssetCSV: {
    method: 'POST',
    path: '/partners/:partnerId/companies/:id/assets',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.instanceof(FormData),
    summary: 'Upload a company asset CSV'
  },
  getAgreements: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/agreements',
    responses: {
      200: z.object({
        items: z.array(Agreement)
      })
    }
  },
  getAgreement: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/agreements/:agreementId',
    responses: {
      200: Agreement,
      ...failedResponses
    }
  },
  createCompanyCode: {
    method: 'POST',
    path: '/companies/code',
    responses: {
      200: CompanyCodeListResponse,
      ...failedResponses
    },
    body: z.object({
      names: z.array(z.string())
    }),
    summary: 'Create a company code'
  },
  getCompanyUsers: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/users',
    responses: {
      200: ListResponse(User),
      ...failedResponses
    },
    summary: 'Get users for a company'
  },
  getMspUsersForCompany: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/mspusers',
    responses: {
      200: ListResponse(User),
      ...failedResponses
    },
    summary: 'Get msp users for a company'
  },
  deleteCompanyUser: {
    method: 'DELETE',
    path: '/partners/:partnerId/companies/:companyId/users/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Delete a user from a company'
  },
  getCompanyHardware: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:companyId/hardware',
    responses: {
      200: Hardware,
      ...failedResponses
    },
    summary: 'Get existing hardware for a company'
  },
  createCompanyHardware: {
    method: 'POST',
    path: '/partners/:partnerId/companies/:companyId/hardware',
    responses: {
      200: Hardware,
      ...failedResponses
    },
    body: z.object({
      manifest: z.any()
    }),
    summary: 'Create a company hardware entry'
  },
  getPartnerHardware: {
    method: 'GET',
    path: '/partners/:partnerId/companies/hardware',
    responses: {
      200: HardwareListResponse,
      ...failedResponses
    },
    summary: 'Get existing hardware for a company'
  },
  getMspDetails: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/mspdetails',
    responses: {
      200: MspDetailsForCompany,
      ...failedResponses
    }
  },
  getSelectedFinanceModels: {
    method: 'GET',
    path: '/msp/:partnerId/customer/:id/selectedfinancemodels',
    responses: {
      200: CustomerSelectedFinanceModel,
      ...failedResponses
    }
  },
  putSelectedFinanceModels: {
    method: 'PUT',
    path: '/msp/:partnerId/customer/:customerId/selectedfinancemodels',
    responses: {
      200: CustomerSelectedFinanceModel,
      ...failedResponses
    },
    body: CustomerSelectedFinanceModelRequest
  },
  getHaloTickets: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/halo-tickets',
    responses: {
      200: z.array(HaloTicketDto),
      ...failedResponses
    }
  },
  getCompanyHaloMapping: {
    method: 'GET',
    path: '/partners/:partnerId/companies/:id/halo-mappings',
    responses: {
      200: HaloMappingDto,
      ...failedResponses
    }
  },
  updateCompanyHaloMapping: {
    method: 'PUT',
    path: '/partners/:partnerId/companies/:id/halo-mappings',
    responses: {
      200: z.undefined(),
      204: z.undefined(),
      ...failedResponses
    },
    body: HaloMappingDto
  },
  getCompanyHaloStock: {
    method: 'GET',
    path: '/msp/:mspId/customers/:customerId/halo-stock',
    responses: {
      200: z.array(HaloStockDto),
      ...failedResponses
    },
    summary: 'Get halo stock for a company'
  },
  getCompanyHaloEquipment: {
    method: 'GET',
    path: '/msp/:mspId/customers/:customerId/halo-equipment',
    responses: {
      200: z.array(HaloEquipmentDto),
      ...failedResponses
    },
    summary: 'Get halo equipment for a company'
  },
  getCustomerBundles: {
    method: 'GET',
    path: '/msp/:mspId/customers/:customerId/customerbundles',
    responses: {
      200: z.object({
        items: z.array(CustomerBundleDto)
      }),
      ...failedResponses
    },
    summary: 'Get bundles for a company'
  }
});
